import React, { useState, useEffect } from 'react';
import { BASE_DOMAIN } from '../utility/baseDomain';
import Modal from './Modal';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import Select from 'react-select';
import { getSelectStyles } from '../utility/selectStyles';
import axiosInstance from "../utility/axiosInstance";
import { useToast } from "./ToastContext";
import { flavorLabel, isFlavorOptionDisabled } from '../utility/flavorOptions';

interface UpgradeToClusterModalProps {
    isOpen: boolean;
    onClose: () => void;
    instance: any | null;
    onUpgraded: () => void;
}

const generateShortId = (length = 8) => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) result += chars[Math.floor(Math.random() * chars.length)];
    return result;
};

const UpgradeToClusterModal: React.FC<UpgradeToClusterModalProps> = ({ isOpen, onClose, instance, onUpgraded }) => {
    const { showToast } = useToast();
    const flavors = useSelector((state: RootState) => state.flavors.data);
    const networks = useSelector((state: RootState) => state.privateNetwork.networks);
    const keypairs = useSelector((state: RootState) => state.keypairs.data);

    const [name, setName] = useState('');
    const [selectedFlavor, setSelectedFlavor] = useState<any>(null);
    const [selectedNetwork, setSelectedNetwork] = useState<any>(null);
    const [selectedKeypair, setSelectedKeypair] = useState<any>(null);
    const [maxSize, setMaxSize] = useState(5);
    // HA mode: 3 managed members behind the LB instead of the single always-on source VM.
    const [haMode, setHaMode] = useState(false);
    const [scaleOutThreshold, setScaleOutThreshold] = useState(70);
    const [scaleInThreshold, setScaleInThreshold] = useState(20);
    const [port, setPort] = useState(80);
    const [startupCommand, setStartupCommand] = useState('');
    const [dnsName, setDnsName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [blockingPlan, setBlockingPlan] = useState<string[]>([]);

    const flavorOptions = flavors.map((fl: any) => ({
        value: fl.id,
        label: flavorLabel(fl),
        isDisabled: !!fl.restricted,
    }));
    const networkOptions = networks.map((net: any) => ({ value: net.id, label: net.name, subnet_id: net.subnet_id }));
    const keypairOptions = keypairs.map((kp: any) => ({ value: kp.name, label: kp.name }));

    // Pre-fill from the instance each time the modal opens for one (flavor + keypair are inherited).
    useEffect(() => {
        if (!isOpen || !instance) return;
        setName(`${(instance.name || 'app').toLowerCase().replace(/[^a-z0-9-]/g, '-')}-cluster`);
        const fl = flavorOptions.find((o: any) => String(o.value) === String(instance.flavor_id));
        setSelectedFlavor(fl ?? null);
        const kp = keypairOptions.find((o: any) => o.value === instance.key_name);
        setSelectedKeypair(kp ?? null);
        setSelectedNetwork(networkOptions.length === 1 ? networkOptions[0] : null);
        setDnsName('');
        setStartupCommand('');
        setBlockingPlan([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, instance]);

    const handleSubmit = async () => {
        if (!instance || !name || !selectedFlavor || !selectedNetwork || !selectedKeypair) {
            showToast('Please fill in all required fields', 'error');
            return;
        }
        setIsSubmitting(true);
        setBlockingPlan([]);
        try {
            await axiosInstance.post('/v1/clusters/from-instance', {
                instance_id: instance.id,
                name,
                flavor: selectedFlavor.value,
                // min_size 0 = hero-as-baseline: the source VM IS the only always-on member, so there
                // are no idle clones to pay for (but a single VM serves, and it is not auto-replaced).
                // HA mode = 3 managed members: spread across 3 hosts, self-healed if one dies, and
                // rolled one at a time on update. The source VM then leaves the pool and is just the
                // image source you deploy to.
                min_size: haMode ? 3 : 0,
                max_size: haMode ? Math.max(3, maxSize) : maxSize,
                scale_out_threshold: scaleOutThreshold / 100,
                scale_in_threshold: scaleInThreshold / 100,
                port,
                backend_network_id: selectedNetwork.value,
                backend_subnet_id: selectedNetwork.subnet_id,
                key_name: selectedKeypair.value,
                dnsName,
                ...(startupCommand.trim() ? { startup_command: startupCommand.trim() } : {}),
            });
            onClose();
            showToast(haMode
                ? 'HA upgrade started - snapshotting the instance (20-60 min for a large disk), then building 3 members across 3 hosts. Watch the Clusters page.'
                : 'Upgrade to cluster started - snapshotting the instance (this can take 20-60 min for a large disk), then building the cluster. Watch the Clusters page.', 'success');
            onUpgraded();
        } catch (error) {
            console.error("Error upgrading to cluster:", error);
            const data = (error as any)?.response?.data;
            const message = data?.error?.message || 'Failed to start upgrade to cluster';
            const plan = Array.isArray(data?.restructure_plan) ? data.restructure_plan : [];
            setBlockingPlan(plan);
            showToast(message, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Upgrade "${instance?.name ?? ''}" to Cluster`} size="md">
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="create-form">
                <p className="note" style={{ marginTop: 0, marginBottom: '16px', color: 'var(--text-muted)' }}>
                    {haMode ? (
                        <>
                            This snapshots <strong>{instance?.name}</strong> and builds a <strong>highly available</strong> cluster
                            behind a load balancer: 3 members on 3 different physical hosts, scaling up to the max below under load.
                            A member that dies is rebuilt automatically within a few minutes, and updates roll one member at a time
                            with little disruption. Your source VM stops serving traffic and becomes the machine you deploy changes to.
                            Each member boots the snapshot, so your app <strong>must start on every boot</strong> (a systemd service
                            or a container restart policy), or give a startup command below.
                        </>
                    ) : (
                        <>
                            This snapshots <strong>{instance?.name}</strong> and puts it behind a load balancer as an autoscaling cluster.
                            Your source VM becomes the always-on baseline member (no extra idle VM to pay for); the group adds more
                            members automatically under load, up to the max below, then scales them back down to just your VM.
                            Each new member boots the snapshot - your app must auto-start (a systemd service), or give a startup command below.
                        </>
                    )}
                </p>
                {blockingPlan.length > 0 && (
                    <div className="note" style={{ marginTop: 0, marginBottom: '16px', color: 'var(--text-muted)' }}>
                        <strong>Before clustering:</strong>
                        <ol style={{ margin: '8px 0 0 18px', padding: 0 }}>
                            {blockingPlan.map((step, i) => <li key={i}>{step}</li>)}
                        </ol>
                    </div>
                )}

                <div className="form-group">
                    <label>Cluster Name</label>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="my-cluster" autoFocus />
                </div>

                <div className="form-group">
                    <label>Flavor <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(inherited from the instance)</span></label>
                    <Select options={flavorOptions} isOptionDisabled={isFlavorOptionDisabled as any} value={selectedFlavor} onChange={setSelectedFlavor} placeholder="Select flavor..." styles={getSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed" />
                </div>

                {/* HA mode. The two models are genuinely different, not a preference:
                    - OFF (min_size 0): the source VM IS the always-on baseline member. Nothing extra
                      runs at rest, but a single VM serves, and it is a plain Nova server so it is NOT
                      auto-replaced if it dies.
                    - ON (min_size 3): three managed members serve behind the LB, spread across three
                      different hosts, and a member that dies is rebuilt automatically (~90s). The
                      source VM steps out of the pool and becomes the image source you deploy to. */}
                <div className="form-group" style={{ border: '1px solid var(--border-strong)', borderRadius: 8, padding: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: haMode ? 10 : 0 }}>
                        <input type="checkbox" checked={haMode} onChange={(e) => setHaMode(e.target.checked)} style={{ width: 16, height: 16 }} />
                        <span style={{ fontWeight: 600 }}>High availability</span>
                        <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(3 members, different hosts, self-healing)</span>
                    </label>
                    {haMode && (
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.86rem', lineHeight: 1.55 }}>
                            Runs <strong>3 members minimum</strong> behind the load balancer, placed on three different
                            physical hosts, scaling up to <strong>{Math.max(3, maxSize)}</strong> under load. If a member is
                            destroyed it is rebuilt automatically within a few minutes, and updates roll one member at a
                            time with little disruption.
                            <br />
                            Your source VM leaves the load balancer and becomes the machine you deploy changes to,
                            so it keeps running and is billed alongside the 3 members.
                        </div>
                    )}
                </div>

                <div className="form-group">
                    <label>
                        Max Instances{' '}
                        <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                            {haMode ? '(the group scales between 3 and this under load)' : '(extra members the group may add on top of your source VM under load)'}
                        </span>
                    </label>
                    <input
                        type="number"
                        value={maxSize}
                        onChange={(e) => setMaxSize(parseInt(e.target.value) || 1)}
                        min={haMode ? 3 : 1}
                    />
                </div>

                <div className="form-group" style={{ display: 'flex', gap: '20px' }}>
                    <div style={{ flex: 1 }}>
                        <label>Scale-Out CPU % <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(default 70)</span></label>
                        <input type="number" value={scaleOutThreshold} onChange={(e) => setScaleOutThreshold(Math.min(100, Math.max(1, parseInt(e.target.value) || 70)))} min="1" max="100" />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label>Scale-In CPU % <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(default 20)</span></label>
                        <input type="number" value={scaleInThreshold} onChange={(e) => setScaleInThreshold(Math.min(99, Math.max(1, parseInt(e.target.value) || 20)))} min="1" max="99" />
                    </div>
                </div>

                <div className="form-group">
                    <label>App Port <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(the port your app listens on - load-balanced + health-checked)</span></label>
                    <input type="number" value={port} onChange={(e) => setPort(parseInt(e.target.value))} min="1" max="65535" />
                </div>

                <div className="form-group">
                    <label>Startup Command <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional - only if your app is not a systemd service)</span></label>
                    <input type="text" value={startupCommand} onChange={(e) => setStartupCommand(e.target.value)} placeholder="e.g. systemctl start myapp  (leave empty if it auto-starts)" />
                </div>

                <div className="form-group">
                    <label>DNS Name (leave empty for a random .redu.cloud)</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <input type="text" value={dnsName} onChange={(e) => setDnsName(e.target.value)} placeholder="e.g., my-cluster.mydomain.com" style={{ flex: 1 }} />
                        <button type="button" onClick={() => setDnsName(`${(name || 'cluster').toLowerCase().replace(/[^a-z0-9-]/g, '-')}-${generateShortId(8)}.${BASE_DOMAIN}`)} className="btn btn-primary" title="Generate a random .redu.cloud domain" style={{ whiteSpace: 'nowrap', color: 'white', height: '40px' }}>
                            ⟳ Generate
                        </button>
                    </div>
                </div>

                <div className="form-group">
                    <label>Network</label>
                    <Select options={networkOptions} value={selectedNetwork} onChange={setSelectedNetwork} placeholder="Select network..." styles={getSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed" />
                </div>

                <div className="form-group">
                    <label>SSH Keypair <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(inherited from the instance)</span></label>
                    <Select options={keypairOptions} value={selectedKeypair} onChange={setSelectedKeypair} placeholder="Select keypair..." styles={getSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed" />
                </div>

                <div className="modal-actions">
                    <button type="button" className="cancel-button" onClick={onClose} disabled={isSubmitting}>Cancel</button>
                    <button type="submit" className="submit-button" disabled={isSubmitting}>
                        {isSubmitting ? 'Starting...' : 'Upgrade to Cluster'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default UpgradeToClusterModal;
